import React, { createContext, useContext, useState, useEffect } from 'react';

const ProductsContext = createContext();

export const useProducts = () => {
  const context = useContext(ProductsContext);
  if (!context) {
    throw new Error('useProducts must be used within a ProductsProvider');
  }
  return context;
};

const defaultProducts = [
  {
    id: 1,
    name: 'Croquettes Premium Chien Adulte',
    description: 'Croquettes haute qualité pour chien adulte, riches en protéines et vitamines.',
    price: 49.99,
    originalPrice: 59.99,
    image: 'https://images.unsplash.com/photo-1589924691995-400dc9ecc119?w=400',
    category: 'alimentation',
    animal: 'chien',
    affiliateUrl: 'https://www.amazon.fr/dp/example1',
    brand: 'Royal Canin',
    rating: 4.8,
    reviews: 234,
    featured: true,
    active: true
  },
  {
    id: 2,
    name: 'Arbre à Chat Design',
    description: 'Arbre à chat moderne avec plusieurs niveaux, griffoirs et hamac intégré.',
    price: 89.99,
    originalPrice: 119.99,
    image: 'https://images.unsplash.com/photo-1545249390-6bdfa286032f?w=400',
    category: 'accessoires',
    animal: 'chat',
    affiliateUrl: 'https://www.amazon.fr/dp/example2',
    brand: 'PetTree',
    rating: 4.6,
    reviews: 156,
    featured: true,
    active: true
  },
  {
    id: 3,
    name: 'Laisse Rétractable 5m',
    description: 'Laisse rétractable jusqu\'à 5 mètres avec système de freinage sécurisé.',
    price: 24.99,
    originalPrice: 29.99,
    image: 'https://images.unsplash.com/photo-1601758228041-f3b2795255f1?w=400',
    category: 'accessoires',
    animal: 'chien',
    affiliateUrl: 'https://www.amazon.fr/dp/example3',
    brand: 'Flexi',
    rating: 4.5,
    reviews: 89,
    featured: false,
    active: true
  },
  {
    id: 4,
    name: 'Fontaine à Eau Automatique',
    description: 'Fontaine à eau filtrée pour chat et petit chien. Capacité 2L.',
    price: 34.99,
    originalPrice: 44.99,
    image: 'https://images.unsplash.com/photo-1574158622682-e40e69881006?w=400',
    category: 'accessoires',
    animal: 'chat',
    affiliateUrl: 'https://www.amazon.fr/dp/example4',
    brand: 'PetSafe',
    rating: 4.7,
    reviews: 312,
    featured: true,
    active: true
  },
  {
    id: 5,
    name: 'Cage de Transport IATA',
    description: 'Cage de transport homologuée IATA pour voyages en avion.',
    price: 79.99,
    originalPrice: 99.99,
    image: 'https://images.unsplash.com/photo-1541599540903-216a46ca1dc0?w=400',
    category: 'transport',
    animal: 'chien',
    affiliateUrl: 'https://www.amazon.fr/dp/example5',
    brand: 'Ferplast',
    rating: 4.4,
    reviews: 67,
    featured: false,
    active: true
  },
  {
    id: 6,
    name: 'Litière Auto-Nettoyante',
    description: 'Litière automatique avec capteur de mouvement et bac amovible.',
    price: 299.99,
    originalPrice: 399.99,
    image: 'https://images.unsplash.com/photo-1514888286974-6c03e2ca1dba?w=400',
    category: 'hygiene',
    animal: 'chat',
    affiliateUrl: 'https://www.amazon.fr/dp/example6',
    brand: 'Litter-Robot',
    rating: 4.9,
    reviews: 445,
    featured: true,
    active: true
  },
  {
    id: 7,
    name: 'Brosse Autonettoyante',
    description: 'Brosse pour chien et chat avec système d\'éjection des poils.',
    price: 19.99,
    originalPrice: 24.99,
    image: 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=400',
    category: 'hygiene',
    animal: 'tous',
    affiliateUrl: 'https://www.amazon.fr/dp/example7',
    brand: 'FURminator',
    rating: 4.6,
    reviews: 523,
    featured: false,
    active: true
  },
  {
    id: 8,
    name: 'Jouet Interactif Intelligent',
    description: 'Balle interactive avec LED et mouvement aléatoire pour stimuler votre animal.',
    price: 29.99,
    originalPrice: 39.99,
    image: 'https://images.unsplash.com/photo-1518020382113-a7e8fc38eac9?w=400',
    category: 'jouets',
    animal: 'tous',
    affiliateUrl: 'https://www.amazon.fr/dp/example8',
    brand: 'Cheerble',
    rating: 4.3,
    reviews: 178,
    featured: true,
    active: true
  }
];

const categories = [
  { id: 'alimentation', label: 'Alimentation', icon: '🍖' },
  { id: 'accessoires', label: 'Accessoires', icon: '🎨' },
  { id: 'hygiene', label: 'Hygiène', icon: '🚿' },
  { id: 'jouets', label: 'Jouets', icon: '🎾' },
  { id: 'transport', label: 'Transport', icon: '🚗' },
  { id: 'sante', label: 'Santé', icon: '💊' }
];

export const ProductsProvider = ({ children }) => {
  const [products, setProducts] = useState(() => {
    const saved = localStorage.getItem('vetordv_products');
    return saved ? JSON.parse(saved) : defaultProducts;
  });

  useEffect(() => {
    localStorage.setItem('vetordv_products', JSON.stringify(products));
  }, [products]);

  const addProduct = (product) => {
    const newProduct = {
      ...product,
      id: Date.now(),
      rating: 0,
      reviews: 0
    };
    setProducts(prev => [...prev, newProduct]);
    return newProduct;
  };

  const updateProduct = (id, updates) => {
    setProducts(prev => prev.map(product => product.id === id ? { ...product, ...updates } : product));
  };

  const deleteProduct = (id) => {
    setProducts(prev => prev.filter(product => product.id !== id));
  };

  const toggleProductActive = (id) => {
    setProducts(prev => prev.map(product => product.id === id ? { ...product, active: !product.active } : product));
  };

  const toggleProductFeatured = (id) => {
    setProducts(prev => prev.map(product => product.id === id ? { ...product, featured: !product.featured } : product));
  };

  const getProductsByCategory = (category) => {
    return products.filter(p => p.category === category && p.active);
  };

  const getProductsByAnimal = (animal) => {
    return products.filter(p => (p.animal === animal || p.animal === 'tous') && p.active);
  };

  const getFeaturedProducts = () => {
    return products.filter(p => p.featured && p.active);
  };

  const getActiveProducts = () => {
    return products.filter(p => p.active);
  };

  const getStats = () => {
    return {
      total: products.length,
      active: products.filter(p => p.active).length,
      featured: products.filter(p => p.featured).length,
      byCategory: categories.map(cat => ({
        ...cat,
        count: products.filter(p => p.category === cat.id).length
      }))
    };
  };

  const resetToDefault = () => {
    setProducts(defaultProducts);
  };

  return (
    <ProductsContext.Provider value={{
      products,
      categories,
      addProduct,
      updateProduct,
      deleteProduct,
      toggleProductActive,
      toggleProductFeatured,
      getProductsByCategory,
      getProductsByAnimal,
      getFeaturedProducts,
      getActiveProducts,
      getStats,
      resetToDefault
    }}>
      {children}
    </ProductsContext.Provider>
  );
};

export default ProductsProvider;
